/* ============================================================
   SINGSPACE ADMIN - MODAL FORMULAIRE UTILISATEUR (Pro Max)
   ============================================================ */

import { AlertCircle, Loader2, Lock, Mail, Shield, ShieldCheck, User, UserCheck, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AdminUser } from '../../hooks/useUsers';
import { Button } from '../ui/button';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  user?: AdminUser | null;
  title: string;
}

export function UserFormModal({ isOpen, onClose, onSubmit, user, title }: UserFormModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'ADMIN' as 'SUPER_ADMIN' | 'ADMIN' | 'MANAGER',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'ADMIN',
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await onSubmit(formData);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Une erreur est survenue lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[32px] shadow-2xl max-w-lg w-full overflow-hidden border border-white/20 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="bg-[#00303c] p-8 text-white relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16" />
          <div className="flex justify-between items-start relative z-10">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={16} className="text-[#00758d]" />
                <span className="text-[10px] font-black uppercase tracking-widest opacity-60">Gestion des accès</span>
              </div>
              <h2 className="text-2xl font-black tracking-tight">{title}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {error && (
            <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-100 rounded-2xl text-rose-600 animate-shake">
              <AlertCircle size={20} className="flex-shrink-0" />
              <p className="text-sm font-bold">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* NOM COMPLET */}
            <div className="space-y-1.5">
              <Label label="Nom complet" icon={User} />
              <input
                type="text"
                className="w-full h-12 px-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-[#00758d] focus:bg-white focus:ring-4 focus:ring-[#00758d]/5 outline-none transition-all font-bold text-sm text-[#00303c]"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Ex: Jean Dupont"
              />
            </div>

            {/* EMAIL */}
            <div className="space-y-1.5">
              <Label label="Email professionnel" icon={Mail} />
              <input
                type="email"
                className="w-full h-12 px-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-[#00758d] focus:bg-white focus:ring-4 focus:ring-[#00758d]/5 outline-none transition-all font-bold text-sm text-[#00303c]"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="email@exemple.com"
              />
            </div>

            {/* MOT DE PASSE */}
            <div className="space-y-1.5">
              <Label label={user ? 'Nouveau mot de passe' : 'Mot de passe'} icon={Lock} />
              <input
                type="password"
                autoComplete="new-password"
                className="w-full h-12 px-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-[#00758d] focus:bg-white focus:ring-4 focus:ring-[#00758d]/5 outline-none transition-all font-bold text-sm text-[#00303c]"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!user}
                minLength={8}
                placeholder="••••••••"
              />
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                Minimum 8 caractères
              </p>
            </div>

            {/* RÔLE */}
            <div className="space-y-1.5">
              <Label label="Attribution des droits" icon={Shield} />
              <div className="relative">
                <select
                  className="w-full h-12 px-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-[#00758d] focus:bg-white focus:ring-4 focus:ring-[#00758d]/5 outline-none transition-all font-bold text-sm text-[#00303c] appearance-none cursor-pointer"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                  required
                >
                  <option value="MANAGER">Manager</option>
                  <option value="ADMIN">Administrateur</option>
                  <option value="SUPER_ADMIN">Super Administrateur</option>
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                  <ShieldCheck size={18} />
                </div>
              </div>

              <div className="mt-2 p-3 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-[#00758d] shadow-sm">
                  {formData.role === 'SUPER_ADMIN' ? <ShieldCheck size={16} /> : <UserCheck size={16} />}
                </div>
                <p className="text-[11px] font-medium text-slate-500 leading-tight">
                  {formData.role === 'SUPER_ADMIN' &&
                    'Contrôle total sur les utilisateurs, la configuration et le système.'}
                  {formData.role === 'ADMIN' && 'Gestion complète des espaces, tarifs et toutes les réservations.'}
                  {formData.role === 'MANAGER' && 'Consultation globale et validation des réservations quotidiennes.'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-50">
            <Button
              type="button"
              variant="ghost"
              className="flex-1 h-12 rounded-2xl font-bold text-slate-400"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="flex-[2] h-12 rounded-2xl bg-[#00303c] hover:bg-[#00758d] text-white font-black shadow-xl shadow-[#00303c]/20 transition-all active:scale-95 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="mr-2" /> Traitement...
                </>
              ) : user ? (
                "Mettre à jour l'accès"
              ) : (
                "Créer l'administrateur"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Label({ label, icon: Icon }: { label: string; icon: any }) {
  return (
    <div className="flex items-center gap-2 mb-1 ml-1">
      <Icon size={12} className="text-[#00758d]" />
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</label>
    </div>
  );
}
